import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PIPELINE_TYPE_LABELS,
  PIPELINE_TYPE_VALUES,
  type PipelineType,
  type PipelineStatus,
} from '../../../types/api.js';

export interface PipelineFilters {
  type: PipelineType | 'all';
  status: PipelineStatus | 'all';
  search: string;
}

export interface PipelineFilterBarProps {
  /** 現在のフィルター値 */
  filters: PipelineFilters;
  /** フィルター変更ハンドラー */
  onFilterChange: (filters: PipelineFilters) => void;
  /** 現在表示中の件数（フィルタ後） */
  resultCount?: number;
  /** 全体件数（フィルタ前） */
  totalCount?: number;
}

/** @graph-connects none */
export const SEARCH_DEBOUNCE_MS = 300;

/** @graph-connects none */
function isDefaultFilters(filters: PipelineFilters): boolean {
  return filters.type === 'all' && filters.status === 'all' && filters.search === '';
}

/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプラインフィルターバー。タイプ・ステータスのセレクト、ジョブ名・作成者・対象DBの横断検索（debounce）、クリア、件数表示を提供
 * @graph-connects none
 */
export function PipelineFilterBar({
  filters,
  onFilterChange,
  resultCount,
  totalCount,
}: PipelineFilterBarProps) {
  const { t } = useTranslation();

  // 検索入力はローカル state で受け、debounce してから URL/親に同期する。
  // 親（URL）から外部リセット（clear ボタン等）が来た場合は input を追従させる。
  const [searchInput, setSearchInput] = useState(filters.search);

  // debounce effect は searchInput だけに依存させたいので、最新の
  // onFilterChange / filters は ref 経由で参照する。filters をそのまま依存配列に
  // 入れると type/status を変えるたびに effect が再走して debounce 時計がリセットされる。
  const onFilterChangeRef = useRef(onFilterChange);
  const filtersRef = useRef(filters);
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
    filtersRef.current = filters;
  });

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  useEffect(() => {
    if (searchInput === filtersRef.current.search) return;
    const id = setTimeout(() => {
      onFilterChangeRef.current({ ...filtersRef.current, search: searchInput });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(id);
    };
  }, [searchInput]);

  // 種別の選択肢は SoT（PIPELINE_TYPE_VALUES / PIPELINE_TYPE_LABELS）から派生させ、
  // 種別追加時にこの直書き配列の更新漏れ（dropdown に出ない silent な乖離）を防ぐ。
  const typeOptions: { value: PipelineType | 'all'; label: string }[] = [
    { value: 'all', label: t('pipeline.filter.all') },
    ...PIPELINE_TYPE_VALUES.map((value) => ({ value, label: PIPELINE_TYPE_LABELS[value] })),
  ];

  const statusOptions: { value: PipelineStatus | 'all'; label: string }[] = [
    { value: 'all', label: t('pipeline.filter.all') },
    { value: 'ACTIVE', label: t('pipeline.active') },
    { value: 'DISABLED', label: t('pipeline.disabled') },
  ];

  const showResultCount = typeof resultCount === 'number' && typeof totalCount === 'number';
  const canClear = !isDefaultFilters(filters);

  return (
    <div className="glass-card p-4 flex flex-col gap-3 animate-content-reveal">
      <div className="flex flex-col sm:flex-row gap-3">
        {/* 検索（複数フィールド横断, debounce） */}
        <div className="flex-1">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
            placeholder={t('pipeline.filter.searchPlaceholder')}
            className="w-full px-3 py-2 rounded-lg text-sm bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] border border-[var(--color-border)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-border-focus)] focus:outline-none transition-colors"
            aria-label={t('common.search')}
          />
        </div>

        {/* タイプフィルター */}
        <select
          value={filters.type}
          onChange={(e) => {
            onFilterChange({ ...filters, type: e.target.value as PipelineType | 'all' });
          }}
          className="px-3 py-2 rounded-lg text-sm bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] border border-[var(--color-border)] focus:border-[var(--color-border-focus)] focus:outline-none transition-colors"
          aria-label={t('pipeline.filter.byType')}
        >
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {/* ステータスフィルター */}
        <select
          value={filters.status}
          onChange={(e) => {
            onFilterChange({ ...filters, status: e.target.value as PipelineStatus | 'all' });
          }}
          className="px-3 py-2 rounded-lg text-sm bg-[var(--color-bg-elevated)] text-[var(--color-text-primary)] border border-[var(--color-border)] focus:border-[var(--color-border-focus)] focus:outline-none transition-colors"
          aria-label={t('pipeline.filter.byStatus')}
        >
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {(showResultCount || canClear) && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-[var(--color-text-muted)]">
            {showResultCount
              ? t('pipeline.filter.resultCount', { count: resultCount, total: totalCount })
              : ''}
          </span>
          {canClear && (
            <button
              type="button"
              onClick={() => {
                onFilterChange({ type: 'all', status: 'all', search: '' });
              }}
              className="text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] underline-offset-2 hover:underline transition-colors"
              aria-label={t('pipeline.filter.clear')}
            >
              {t('pipeline.filter.clear')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
